import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  ErrorOutline,
  PlayArrow,
  RemoveCircleOutline,
  UploadFileOutlined,
  ContentPasteOutlined,
} from '@mui/icons-material';
import { useUsage } from '../../context/UsageContext';
import { hasReachedLimit } from '../../services/usageService';
import {
  getDefaultExtractionConfig,
  isExtractionConfigValid,
  extractionConfigToLegacyExtractionTypeArray,
} from '../../utils/extractionTaxonomy';
import ExtractionConfigFormSection from '../ExtractionConfigFormSection';
import FileDropZone from './FileDropZone';

/**
 * Empty single-session form.
 * @returns {object} Form state.
 */
function emptyForm() {
  return {
    name: '',
    description: '',
    file: null,
    pastedContent: '',
    contentType: 'file',
    extractionConfig: getDefaultExtractionConfig(),
    targetedExtraction: '',
  };
}

/**
 * Empty batch row.
 * @returns {object} Batch row state.
 */
function emptyBatchRow() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    description: '',
    file: null,
    targetedExtraction: '',
    status: 'pending', // pending | processing | completed | error
    error: null,
  };
}

/**
 * Friendlier text for a batch extraction failure.
 * @param {Error} error - Error.
 * @returns {string} Message.
 */
function describeBatchError(error) {
  const message = error?.message || '';
  if (!message) return 'Failed to process session';
  if (message.includes('JSON')) return 'AI response formatting error - content may have been partially extracted. Please check the session results.';
  if (message.includes('quota') || message.includes('limit')) return 'API quota exceeded. Please try again later or check your API settings.';
  if (message.includes('network') || message.includes('fetch')) return 'Network error. Please check your connection and try again.';
  return message;
}

/**
 * Whether a single-session form can be submitted.
 * @param {object} form - Form state.
 * @returns {boolean} True when valid.
 */
function isSingleFormValid(form) {
  return Boolean(
    form.name.trim() &&
    (form.contentType !== 'file' || form.file) &&
    (form.contentType !== 'paste' || form.pastedContent.trim()) &&
    isExtractionConfigValid(form.extractionConfig)
  );
}

/**
 * "New session" dialog: one session (file or pasted text) or a batch of
 * files that share the same extraction settings. Each submitted session is
 * created and sent for server-side requirement extraction.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Close handler.
 * @param {Function} props.onCreateSession - async (formData) => void (ProjectDetail handler).
 * @param {Function} props.onLimitReached - Called when the plan's session limit is hit.
 * @param {boolean} props.extracting - Whether an extraction dispatch is in flight.
 * @param {string} [props.extractionError] - Last extraction error.
 * @returns {JSX.Element}
 */
function CreateSessionDialog({ open, onClose, onCreateSession, onLimitReached, extracting, extractionError }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshUsage } = useUsage();
  const [tab, setTab] = useState(0); // 0 single, 1 batch
  const [form, setForm] = useState(emptyForm);
  const [batchRows, setBatchRows] = useState(() => [emptyBatchRow()]);
  const [batchRunning, setBatchRunning] = useState(false);

  // Fresh form whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setTab(0);
      setForm(emptyForm());
      setBatchRows([emptyBatchRow()]);
    }
  }, [open]);

  const patchForm = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);

  const handleSingleFile = useCallback((file) => {
    setForm((prev) => ({
      ...prev,
      file,
      // Auto-fill the session name from the file name when it is empty.
      name: prev.name.trim() ? prev.name : file.name.replace(/\.[^/.]+$/, ''),
    }));
  }, []);

  const updateBatchRow = useCallback((id, patch) => {
    setBatchRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const handleCreateSingle = async () => {
    if (!isSingleFormValid(form)) return;
    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'sessions')) {
      onLimitReached();
      return;
    }
    const result = await onCreateSession(form);
    refreshUsage();
    // Keep the dialog open on failure so the error and the form stay visible.
    if (result?.success !== false) onClose();
  };

  /**
   * Create and extract one batch row.
   * @param {object} row - Batch row.
   * @returns {Promise<boolean>} False when the session limit stopped the batch.
   */
  const processBatchRow = useCallback(async (row) => {
    if (!row.file || !row.name.trim() || !isExtractionConfigValid(form.extractionConfig)) return true;

    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'sessions')) {
      onLimitReached();
      updateBatchRow(row.id, { status: 'error', error: 'Session limit reached. Upgrade your plan.' });
      return false;
    }

    updateBatchRow(row.id, { status: 'processing', error: null });
    try {
      const result = await onCreateSession({
        name: row.name,
        description: row.description,
        file: row.file,
        contentType: 'file',
        extractionConfig: form.extractionConfig,
        extractionType: extractionConfigToLegacyExtractionTypeArray(form.extractionConfig),
        targetedExtraction: row.targetedExtraction,
      });
      refreshUsage();
      if (result?.success === false) throw new Error(result.error || 'Failed to process session');
      updateBatchRow(row.id, { status: 'completed' });
    } catch (error) {
      console.error('Batch session processing error:', error);
      updateBatchRow(row.id, { status: 'error', error: describeBatchError(error) });
    }
    return true;
  }, [form.extractionConfig, onCreateSession, onLimitReached, refreshUsage, updateBatchRow]);

  const readyRows = batchRows.filter((r) => r.file && r.name.trim() && r.status !== 'completed' && r.status !== 'processing');

  const handleProcessAll = async () => {
    setBatchRunning(true);
    try {
      for (const row of readyRows) {
        const keepGoing = await processBatchRow(row);
        if (!keepGoing) break;
      }
    } finally {
      setBatchRunning(false);
    }
  };

  const busy = extracting || batchRunning || batchRows.some((r) => r.status === 'processing');
  const configValid = isExtractionConfigValid(form.extractionConfig);

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      disableRestoreFocus
      aria-labelledby="create-session-title"
    >
      <DialogTitle id="create-session-title">New session</DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="Session type">
          <Tab label="Single session" />
          <Tab label="Batch upload" />
        </Tabs>
      </Box>
      <DialogContent>
        {extractionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extractionError}
          </Alert>
        )}

        {tab === 0 && (
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.contentType}
              onChange={(e, value) => {
                if (!value) return;
                patchForm({
                  contentType: value,
                  file: value === 'paste' ? null : form.file,
                  pastedContent: value === 'file' ? '' : form.pastedContent,
                });
              }}
              aria-label="Content source"
            >
              <ToggleButton value="file"><UploadFileOutlined fontSize="small" sx={{ mr: 0.75 }} />Upload file</ToggleButton>
              <ToggleButton value="paste"><ContentPasteOutlined fontSize="small" sx={{ mr: 0.75 }} />Paste text</ToggleButton>
            </ToggleButtonGroup>

            {form.contentType === 'file' ? (
              <FileDropZone file={form.file} onFile={handleSingleFile} label="Session file" />
            ) : (
              <TextField
                label="Content"
                fullWidth
                multiline
                minRows={6}
                value={form.pastedContent}
                onChange={(e) => patchForm({ pastedContent: e.target.value })}
                placeholder="Paste meeting notes, a transcript, user stories or any text to extract requirements from"
              />
            )}

            <TextField
              autoFocus
              label="Session name"
              fullWidth
              value={form.name}
              onChange={(e) => patchForm({ name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => patchForm({ description: e.target.value })}
            />

            <ExtractionConfigFormSection
              value={form.extractionConfig}
              onChange={(next) => patchForm({ extractionConfig: next })}
            />

            <TextField
              label="Focus (optional)"
              fullWidth
              multiline
              minRows={2}
              value={form.targetedExtraction}
              onChange={(e) => patchForm({ targetedExtraction: e.target.value })}
              placeholder="e.g. user authentication, payment processing, mobile layout"
              helperText="Leave empty to extract every requirement from the content."
            />
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload several files at once. Each file becomes its own session; the extraction settings apply to all of them.
            </Typography>

            <ExtractionConfigFormSection
              value={form.extractionConfig}
              onChange={(next) => patchForm({ extractionConfig: next })}
            />

            {batchRows.map((row, index) => {
              const locked = row.status === 'processing' || row.status === 'completed';
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: 1,
                    borderColor: row.status === 'error' ? 'error.main' : 'divider',
                    borderRadius: 'var(--q-radius-xl)',
                    p: 2,
                    display: 'grid',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>File {index + 1}</Typography>
                    {row.status === 'completed' && <Chip icon={<CheckCircle />} label="Sent for extraction" size="small" color="success" />}
                    {row.status === 'processing' && <Chip icon={<CircularProgress size={12} />} label="Extracting" size="small" color="primary" />}
                    {row.status === 'error' && <Chip icon={<ErrorOutline />} label="Error" size="small" color="error" />}
                    {batchRows.length > 1 && (
                      <Tooltip title="Remove">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remove file ${index + 1}`}
                            onClick={() => setBatchRows((rows) => rows.filter((r) => r.id !== row.id))}
                            disabled={row.status === 'processing'}
                          >
                            <RemoveCircleOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '240px 1fr' } }}>
                    <FileDropZone
                      compact
                      file={row.file}
                      disabled={locked}
                      label={`File ${index + 1}`}
                      onFile={(file) => updateBatchRow(row.id, {
                        file,
                        name: row.name.trim() ? row.name : file.name.replace(/\.[^/.]+$/, ''),
                      })}
                    />
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      <TextField
                        label="Session name"
                        fullWidth
                        value={row.name}
                        onChange={(e) => updateBatchRow(row.id, { name: e.target.value })}
                        disabled={locked}
                      />
                      <TextField
                        label="Description (optional)"
                        fullWidth
                        value={row.description}
                        onChange={(e) => updateBatchRow(row.id, { description: e.target.value })}
                        disabled={locked}
                      />
                    </Box>
                  </Box>
                  {row.error && <Alert severity="error">{row.error}</Alert>}
                  {row.status !== 'completed' && (
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => processBatchRow(row)}
                        disabled={!row.file || !row.name.trim() || !configValid || row.status === 'processing' || batchRunning}
                        startIcon={row.status === 'processing' ? <CircularProgress size={14} /> : <PlayArrow />}
                      >
                        {row.status === 'processing' ? 'Extracting...' : 'Extract this file'}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}

            <Box>
              <Button startIcon={<Add />} onClick={() => setBatchRows((rows) => [...rows, emptyBatchRow()])}>
                Add another file
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {tab === 1 && batchRows.some((r) => r.status === 'completed') ? 'Done' : 'Cancel'}
        </Button>
        {tab === 0 ? (
          <Button
            onClick={handleCreateSingle}
            variant="contained"
            disabled={!isSingleFormValid(form) || extracting}
            startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
          >
            {extracting ? 'Extracting...' : 'Create & extract'}
          </Button>
        ) : (
          <Button
            onClick={handleProcessAll}
            variant="contained"
            disabled={!readyRows.length || !configValid || busy}
            startIcon={batchRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
          >
            {batchRunning ? 'Extracting...' : `Extract ${readyRows.length || ''} file${readyRows.length === 1 ? '' : 's'}`.replace('  ', ' ')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CreateSessionDialog;
